import { cloneDeep, startCase } from "lodash";
import type {
  ProcessGroup,
  Process,
  TrendsPageFilterStateType,
  ManyRollupsWithTagsArrays,
  TimeRangeFinite,
  PnlChartSectionType,
} from "../../../types/types";
import { PnlCategoriesEnum } from "../../../types/enums";
import { objectFromEntries } from "@raven/shared/utils";
import {
  rollupSingleProcessBreakdownCharts,
  NOT_SCHEDULED_TITLE,
} from "./pnlSummaryChartUtils";
import { CATEGORY_TO_LABEL_MAP } from "../../../constants/constants";
import { calcPnlChartCategories } from "./calcPnlChartCategories";
import { addShiftlyRollupsIntoSingleRollup } from "../TrendChart/trendChartUtils";
import { parseTrendsPageFilterState } from "../../FlightTrends/trendsPageUtils";
import { getFilteredProcessesFromFilterState } from "../../FlightTrends/TrendsFilters/filtersDrawerUtils";

export const CATEGORIES_NOT_AFFECTING_BAR_WIDTHS = [
  "notScheduled",
  "plantLoss",
  "uncollected",
  // "producing" is accounted for in the function,
  // depending if it's a "producing tags" chart
];

const EMPTY_CHART_SECTION = {
  title: "",
  time: 0,
  count: 0,
  percent: 0,
  bars: [],
} as PnlChartSectionType;

type PnlChartBarType = PnlChartSectionType & { time: number };

export const rollupSPChartsSplitByMachine = ({
  chartsFilteredByTag,
  chartTitle,
  totalTime,
  hiddenPnlChartCategories,
}: {
  chartsFilteredByTag: PnlChartSectionType[][];
  chartTitle: string;
  totalTime: number;
  hiddenPnlChartCategories: string[];
}): PnlChartSectionType[] => {
  let biggestBarTime = 0;

  const isChartWithProducingTags = Boolean(
    chartsFilteredByTag.find(chartsArr =>
      Boolean(chartsArr.find(chart => chart.title === "Producing"))
    )
  );

  const rolledUpChart = chartsFilteredByTag.reduce(
    (acc: PnlChartBarType, chartToRollUp: PnlChartSectionType[]) => {
      chartToRollUp.forEach(pnlCategoryChartSection => {
        // the accumulator only has one section, acc
        // (we don't copy it and return the copy,
        // we initialize then explicitly mutate it)

        // add the sum of bar times from this section
        acc.time += pnlCategoryChartSection.bars.reduce(
          (accum, bar) => accum + bar.time,
          0
        );
        acc.count = (acc.count || 0) + 1;

        pnlCategoryChartSection.bars.forEach(bar => {
          const barInAcc = acc.bars.find(accBar => accBar.title === bar.title);
          // initialize the bar if it doesn't exist
          if (!barInAcc) {
            acc.bars.push({
              ...bar,
              // title:
              percent: 0,
              count: 1,
              percentWidth: 0,
            });
          } else {
            // otherwise add the time + count
            barInAcc.time += bar.time;
            barInAcc.count = (barInAcc.count || 0) + 1;
          }

          if (
            // if there's only one category (e.g. "Producing"), count all bar widths
            chartToRollUp.length === 1 ||
            // otherwise filter out categories that don't affect bar widths
            ![
              ...CATEGORIES_NOT_AFFECTING_BAR_WIDTHS,
              ...(isChartWithProducingTags ? [] : ["producing"]),
              ...hiddenPnlChartCategories.map(
                category => CATEGORY_TO_LABEL_MAP[category]
              ),
            ].includes(pnlCategoryChartSection.title)
          ) {
            // track biggestBarTime to recalculate the bar widths/percents
            biggestBarTime = Math.max(
              biggestBarTime,
              barInAcc ? barInAcc.time : bar.time
            );
          }
        });
      });
      return acc;
    },

    {
      title: chartTitle,
      time: 0,
      percent: 0,
      count: 0,
      bars: [],
    }
  );

  // recalculate the rolled-up bar percents/widths
  rolledUpChart.percent = rolledUpChart.time / totalTime;
  rolledUpChart.bars.forEach(bar => {
    bar.percent = bar.time / rolledUpChart.time;
    bar.percentWidth = bar.time / biggestBarTime;
  });

  return [rolledUpChart];
};

// tslint:disable-next-line: cognitive-complexity
export function filterPnlChartsForTrendsPage({
  trendsPageFilterState,
  visibleGroups,
  SPChartsArr,
  visibleProcesses,
  hiddenPnlChartCategories,
  focusRange,
  allRollupsMetricTrendChartBarsArrays,
}: {
  allRollupsMetricTrendChartBarsArrays: ManyRollupsWithTagsArrays;
  trendsPageFilterState: TrendsPageFilterStateType;
  visibleGroups: ProcessGroup[];
  SPChartsArr: [string, PnlChartSectionType[]][];
  visibleProcesses: Process[];
  hiddenPnlChartCategories: PnlCategoriesEnum[];
  focusRange: TimeRangeFinite | null;
}) {
  // the focus range is the visible part of the timeline
  // each process spans the entire focus range
  // hence, each process has time = (focusRange length) or (focusRange start -> now)
  const totalTimePerProcess = !focusRange
    ? 0
    : Math.min(Date.now(), focusRange.lt) - focusRange.ge;

  const numSPCharts = SPChartsArr.length;
  const totalTimeNotAffectingPercents =
    getTotalTimeNotAffectingPercents(SPChartsArr);
  const totalTime =
    totalTimePerProcess * numSPCharts - totalTimeNotAffectingPercents;

  const { tag, shiftName, pnlCategory } = trendsPageFilterState;
  const {
    isTrendsPageFilteredByTag,
    isTrendsPageFilteredByShift,
    isTrendsPageFilteredByPnlCategory,
  } = parseTrendsPageFilterState({ trendsPageFilterState });

  const filteredProcessIds = getFilteredProcessesFromFilterState(
    trendsPageFilterState,
    visibleGroups,
    visibleProcesses
  ).map(p => p.id);

  // if filtered by shift, we roll up the shiftly rollups using addShiftlyRollups
  const SPChartsArrFilteredByShift =
    // only calculate if we need them
    isTrendsPageFilteredByShift
      ? Object.entries(allRollupsMetricTrendChartBarsArrays).map(
          ([processId, rollupsArrForAllShifts]) => {
            const rollupsForShift = rollupsArrForAllShifts.filter(
              ({ shift }) => shift?.name === shiftName
            );

            // add all the rollups of that shift together over the whole focus range
            const summedShiftlyRollup = addShiftlyRollupsIntoSingleRollup({
              rollupsArr: rollupsForShift,
              focusRange,
              shiftName,
            });

            // calc pnl summary chart rows from the shift-summed rollup
            const pnlSummarChartRows = calcPnlChartCategories(
              summedShiftlyRollup,
              hiddenPnlChartCategories
            );

            return [processId, pnlSummarChartRows] as [
              string,
              PnlChartSectionType[],
            ];
          }
        )
      : [];

  const chartsToFilterByProcess = isTrendsPageFilteredByShift
    ? // add the trend chart bars shiftly rollups, filtered by shift
      // (these rollups are one-per-shift, so we can roll them up here over the full focus range)
      SPChartsArrFilteredByShift
    : SPChartsArr;

  // roll up singleProcessBreakdownCharts charts based on selectedProcessIdsRef
  const selectedChartsObj: {
    [processId: string]: PnlChartSectionType[];
  } = objectFromEntries(
    chartsToFilterByProcess.filter(([processId, chart]) =>
      filteredProcessIds.includes(processId)
    )
  ) as {
    [processId: string]: PnlChartSectionType[];
  };

  const allPnlCharts =
    Object.values(selectedChartsObj).length === 0
      ? []
      : rollupSingleProcessBreakdownCharts(
          Object.values(selectedChartsObj),
          hiddenPnlChartCategories,
          false // don't show quality on trends page
        );

  // if filtered by shift, filter then roll up the trend chart bars arrays
  if (isTrendsPageFilteredByTag) {
    // make Pnl chart with rows for each machine
    // starting with the WHOLE Pnl chart for each machine (selectedChartsObj)

    // 1. filter down each chart to just one tag
    const chartsFilteredByTag: {
      [processId: string]: PnlChartSectionType[];
    } = objectFromEntries(
      Object.entries(selectedChartsObj).map(
        // iterate over [processId, chartsArr]
        // for one process at a time
        ([processId, chartsArr]: [string, PnlChartSectionType[]]) => {
          // 1. find the chartsArr that has the tag in its bars array
          const chartInPnlCategoryWithOurTag = chartsArr.find(({ bars }) =>
            bars.map(bar => bar.title).includes(tag)
          );

          // 2. remove all the other bars except our tag
          const clonedChart: PnlChartSectionType = chartInPnlCategoryWithOurTag
            ? cloneDeep(chartInPnlCategoryWithOurTag)
            : EMPTY_CHART_SECTION;

          const process = visibleProcesses.find(p => p.id === processId);
          const processName = process ? process.name : processId;

          const barOfThisTag = clonedChart.bars.find(bar => bar.title === tag);

          const chartSectionWithOnlyOurTag = {
            // e.g. Producing or Unplanned section
            // contains a bunch of bars, one for each tag
            ...clonedChart,
            ...(chartInPnlCategoryWithOurTag
              ? // if we have bars, find the one with our tag
                // then override the cloned bars (there should only be one bar)
                {
                  bars: [
                    {
                      ...barOfThisTag,
                      title: processName,
                    },
                  ],
                }
              : {}),
          };
          return [processId, [chartSectionWithOnlyOurTag]];
        }
      )
    ) as {
      [processId: string]: PnlChartSectionType[];
    };

    // once all other data is removed, return the rolled up version (rollupSPChartsSplitByMachine)
    return Object.values(chartsFilteredByTag).length === 0
      ? []
      : rollupSPChartsSplitByMachine({
          chartsFilteredByTag: Object.values(chartsFilteredByTag),
          chartTitle: tag,
          totalTime,
          hiddenPnlChartCategories,
        });
  } else if (isTrendsPageFilteredByPnlCategory) {
    const pnlCategoryTitle =
      pnlCategory === "notScheduled"
        ? NOT_SCHEDULED_TITLE
        : startCase(pnlCategory);
    // make Pnl chart with rows for each machine
    // starting with the WHOLE Pnl chart for each machine (selectedChartsObj)

    // 1. filter down each chart to just one tag
    const chartsFilteredByCategory: {
      [processId: string]: PnlChartSectionType[];
    } = objectFromEntries(
      Object.entries(selectedChartsObj).map(
        // iterate over [processId, chartsArr]
        // for one process at a time
        ([processId, chartsArr]: [string, PnlChartSectionType[]]) => {
          // 1. find the chartsArr that has the tag in its bars array
          const chartInPnlCategoryWithOurCategory = chartsArr.find(
            ({ title }) => title === pnlCategoryTitle
          );

          // 2. remove all the other bars except our tag
          const chartSectionWithOnlyOurCategory: PnlChartSectionType =
            chartInPnlCategoryWithOurCategory
              ? cloneDeep(chartInPnlCategoryWithOurCategory)
              : EMPTY_CHART_SECTION;

          return [processId, [chartSectionWithOnlyOurCategory]];
        }
      )
    ) as {
      [processId: string]: PnlChartSectionType[];
    };

    // once all other data is removed, return the rolled up version (rollupSPChartsSplitByMachine)
    return Object.values(chartsFilteredByCategory).length === 0
      ? []
      : rollupSPChartsSplitByMachine({
          chartsFilteredByTag: Object.values(chartsFilteredByCategory),
          chartTitle: pnlCategoryTitle,
          totalTime,
          hiddenPnlChartCategories,
        });
  } else {
    // roll up all our charts together into one
    return allPnlCharts;
  }
}

function getTotalTimeNotAffectingPercents(
  SPChartsArr: [string, PnlChartSectionType[]][]
): number {
  return SPChartsArr.reduce(
    (acc, [processId, pnlBreakdownCharts]) =>
      acc + getTimeNotAffectingPercents(pnlBreakdownCharts),
    0
  );
}

function getTimeNotAffectingPercents(
  pnlBreakdownCharts: PnlChartSectionType[]
): number {
  return pnlBreakdownCharts.reduce(
    (acc, pnlChart) =>
      acc +
      pnlChart.bars.reduce(
        (barAcc, bar) =>
          barAcc +
          (CATEGORIES_NOT_AFFECTING_BAR_WIDTHS.includes(bar.category)
            ? bar.time
            : 0),
        0
      ),
    0
  );
}
